"use client";

import * as React from "react";
import { cn } from "@/lib/utils";
import { Button } from "../ui/button";
import { XMarkIcon } from "@heroicons/react/24/outline";

export interface FileUploadProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type" | "onChange"> {
  onFileChange?: (file: File | null) => void;
  acceptedFormats?: string[];
  maxSize?: number; // in bytes
  error?: string;
}

const FileUpload = React.forwardRef<HTMLInputElement, FileUploadProps>(
  ({ className, onFileChange, acceptedFormats = [".pdf", ".doc", ".docx"], maxSize, error, ...props }, ref) => {
    const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
    const [isDragging, setIsDragging] = React.useState(false);
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    const handleFileSelect = (file: File | null) => {
      setSelectedFile(file);
      if (onFileChange) {
        onFileChange(file);
      }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0] || null;
      handleFileSelect(file);
    };

    const handleRemove = (e: React.MouseEvent) => {
      e.stopPropagation();
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      if (onFileChange) {
        onFileChange(null);
      }
    };

    const handleDragOver = (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files?.[0] || null;
      if (file) {
        handleFileSelect(file);
        if (fileInputRef.current) {
          const dataTransfer = new DataTransfer();
          dataTransfer.items.add(file);
          fileInputRef.current.files = dataTransfer.files;
        }
      }
    };

    const formatFileSize = (bytes: number): string => {
      if (bytes === 0) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
    };

    return (
      <div className='w-full'>
        <div
          className={cn(
            "border-border-primmary flex min-h-[120px] w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed bg-transparent px-4 py-6 transition-colors",
            isDragging && "border-link-primary bg-primary-input-bg",
            error && "border-border-destructive",
            className
          )}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={(node) => {
              fileInputRef.current = node;
              if (typeof ref === "function") {
                ref(node);
              } else if (ref) {
                ref.current = node;
              }
            }}
            type='file'
            accept={acceptedFormats.join(",")}
            onChange={handleFileChange}
            className='hidden'
            {...props}
          />

          {selectedFile ? (
            <div className='flex w-full items-center justify-between gap-3'>
              <div className='flex min-w-0 flex-1 flex-col items-start gap-1'>
                <p className='text-text-title w-full truncate text-sm font-medium'>{selectedFile.name}</p>
                <p className='text-text-muted text-xs'>{formatFileSize(selectedFile.size)}</p>
              </div>
              <Button
                type='button'
                variant='ghost'
                size='sm'
                onClick={handleRemove}
                className='hover:bg-destructive/10 shrink-0'
              >
                <XMarkIcon className='text-icon-destructive size-5' />
              </Button>
            </div>
          ) : (
            <div className='text-center'>
              <svg
                className='text-text-muted mx-auto mb-3 size-10'
                xmlns='http://www.w3.org/2000/svg'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={1.5}
                  d='M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12'
                />
              </svg>
              <p className='text-text-title mb-1 text-sm font-medium'>
                <span className='text-link-primary'>Click to upload</span> or drag and drop
              </p>
              <p className='text-text-muted text-xs'>
                {acceptedFormats.join(", ").toUpperCase()}
                {maxSize && ` (Max ${formatFileSize(maxSize)})`}
              </p>
            </div>
          )}
        </div>
        {error && <p className='text-text-destructive mt-1.5 text-xs'>{error}</p>}
      </div>
    );
  }
);

FileUpload.displayName = "FileUpload";

export { FileUpload };
